import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { purchaseService } from '../../services/purchaseService'

interface Purchase {
  purchase_id: number
  purchase_number: string
  purchase_date: string
  expected_delivery_date: string | null
  status: string
  total_amount: number
  total_amount_base: number
  currency_code: string
  exchange_rate: number
  suppliers: {
    company_name: string
  } | null
  users: {
    first_name: string
    last_name: string
  } | null
  purchase_items: { item_id: number }[]
}

export const PurchasesPage = () => {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery({
    queryKey: ['purchases', search, statusFilter, page],
    queryFn: () => purchaseService.getAll({ status: statusFilter, page, limit: 20 }),
  })

  const purchases: Purchase[] = data?.data?.data?.items || []
  const pagination = data?.data?.data?.pagination

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'received': return 'bg-green-100 text-green-800'
      case 'ordered': return 'bg-blue-100 text-blue-800'
      case 'partial': return 'bg-yellow-100 text-yellow-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'received': return CheckCircleIcon
      case 'ordered': return TruckIcon
      case 'partial': return ClockIcon
      case 'cancelled': return XCircleIcon
      default: return ClipboardDocumentListIcon
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchases</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage purchase orders and stock receipts
          </p>
        </div>
        <Link
          to="/purchases/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <PlusIcon className="w-5 h-5" />
          New Purchase
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search purchase orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="ordered">Ordered</option>
            <option value="partial">Partially Received</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Purchases List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : purchases.length === 0 ? (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <ClipboardDocumentListIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No purchase orders found</h3>
          <p className="text-gray-500 mb-4">
            {search ? 'Try adjusting your search terms' : 'Get started by creating your first purchase order'}
          </p>
          {!search && (
            <Link
              to="/purchases/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              <PlusIcon className="w-5 h-5" />
              New Purchase
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {purchases.map((purchase) => {
                    const StatusIcon = getStatusIcon(purchase.status)
                    return (
                      <tr key={purchase.purchase_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link 
                            to={`/purchases/${purchase.purchase_id}`}
                            className="text-sm font-medium text-primary-600 hover:underline"
                          >
                            {purchase.purchase_number}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">
                            {purchase.suppliers?.company_name || 'Unknown Supplier'}
                          </p>
                          <p className="text-xs text-gray-500">
                            By {purchase.users?.first_name} {purchase.users?.last_name}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-600">
                            {new Date(purchase.purchase_date).toLocaleDateString()}
                          </p>
                          {purchase.expected_delivery_date && (
                            <p className="text-xs text-gray-500">
                              Delivery: {new Date(purchase.expected_delivery_date).toLocaleDateString()}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {purchase.purchase_items.length} items
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-900">
                            {purchase.currency_code} {Number(purchase.total_amount).toLocaleString()}
                          </p>
                          {purchase.currency_code !== 'KES' && (
                            <p className="text-xs text-gray-500">
                              KSh {Number(purchase.total_amount_base).toLocaleString()}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(purchase.status)}`}>
                            <StatusIcon className="w-3 h-3" />
                            {purchase.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/purchases/${purchase.purchase_id}`}
                            className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                          >
                            {purchase.status === 'draft' || purchase.status === 'ordered' || purchase.status === 'partial' 
                              ? 'View / Receive' 
                              : 'View'}
                          </Link>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl shadow border border-gray-100 p-4">
              <p className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} purchases
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                  className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
